import type { StoryBrief } from '../api/types';
import styles from './CatchMeUpCard.module.css';

interface CatchMeUpCardProps {
  brief: StoryBrief;
  /** Scrolls the returning contributor to the prompt / compose surface. */
  onJumpIn?: () => void;
  /**
   * "Got it" — acknowledges the catch-up without navigating anywhere.
   *
   * Load-bearing, not decorative: "Jump in" only renders when there is a prompt
   * to jump to, so without this a returning contributor on a prompt-less phase
   * would have no way to acknowledge and their read cursor would never advance.
   */
  onDismiss?: () => void;
}

/**
 * Whether this brief has anything to catch up on. Exported so the page can gate
 * first-visit onboarding on the same predicate the card uses — two definitions
 * would drift, and the drift shows up as both cards rendering at once.
 */
export function hasCatchUp(brief: StoryBrief | null): boolean {
  const since = brief?.sinceLastVisit;
  return !!since && (since.newTakeCount > 0 || !!since.newPhase || !!since.newRecapPhaseName);
}

/**
 * "Catch Me Up" — the returning-contributor re-entry surface. Shows where the
 * story stands plus what changed since the reader's last visit, so a phase
 * prompt after days away lands as an invitation, not a cold open.
 *
 * Renders only when there is something to catch up on (new takes, a new phase,
 * or a new Highlight since the last visit). First-time visitors get
 * ThreadWelcomeCard instead — the two never show together.
 */
export function CatchMeUpCard({ brief, onJumpIn, onDismiss }: CatchMeUpCardProps) {
  const since = brief.sinceLastVisit;
  if (!hasCatchUp(brief)) return null;

  const takes = since!.newTakeCount;
  const people = since!.newContributorCount;
  const takesLabel = takes === 1 ? '1 new take' : `${takes} new takes`;
  const peopleLabel = people === 1 ? '1 person' : `${people} people`;

  return (
    <section class={styles.card} aria-label="Catch me up">
      <div class={styles.header}>
        <span class={styles.spark} aria-hidden="true">✦</span>
        <span class={styles.title}>Catch me up</span>
      </div>

      {takes > 0 && (
        <p class={styles.delta}>
          <strong>{takesLabel}</strong> from {peopleLabel} since you were last here.
        </p>
      )}

      {(since!.newPhase || since!.newRecapPhaseName) && (
        <div class={styles.chips}>
          {since!.newPhase && (
            <span class={styles.chip}>Now on “{since!.newPhase}”</span>
          )}
          {since!.newRecapPhaseName && (
            <span class={styles.chipCalm}>New Highlight: {since!.newRecapPhaseName}</span>
          )}
        </div>
      )}

      <p class={styles.stand}>{brief.whereThingsStand}</p>

      <div class={styles.actions}>
        {brief.cta.promptText && (
          <button type="button" class={styles.jumpIn} onClick={onJumpIn}>
            Jump in
          </button>
        )}
        {onDismiss && (
          <button type="button" class={styles.dismiss} onClick={onDismiss}>
            Got it
          </button>
        )}
      </div>
    </section>
  );
}
